import { selectedOptionsString } from '@repo/shopify/options'
import { CartItem as CartItemType } from '@repo/shopify/types'
import PriceWithDiscount from '@repo/ui/components/price-with-discount'
import { cn } from '@repo/ui/lib/utils'
import { createHrefForDocument, createHrefWithQuery } from '@repo/url/href'
import Image from 'next/image'
import Link from 'next/link'
import { FunctionComponent } from 'react'
import { useCart } from '../../../providers/CartProvider'
import { PageDto } from '../../../types/page.types'
import { EditCartQuantity } from '../../cart/EditCartQuantity'
import RemoveFromCart from '../../cart/RemoveFromCart'

interface Props {
  pageDto: PageDto
  item: CartItemType
  closeCart: () => void
  className?: string
}

const CartItem: FunctionComponent<Props> = ({ pageDto, item, closeCart, className }) => {
  const { updateCartItem } = useCart()

  const merchandiseSearchParams: Record<string, string> = item.merchandise.selectedOptions.reduce(
    (acc, { name, value }) => {
      if (value !== 'Default Title') {
        acc[name.toLowerCase()] = value
      }

      return acc
    },
    {} as Record<string, string>,
  )

  const discount =
    item?.discountAllocations
      ?.map((discount) => discount?.discountedAmount?.amount)
      ?.reduce((accumulator, currentValue) => {
        return accumulator + parseFloat(currentValue)
      }, 0) / item.quantity

  const amount = parseFloat(item.cost.totalAmount.amount)
  const href = createHrefForDocument('product', item.merchandise.product.handle, pageDto.params.locale) || ''
  const url = createHrefWithQuery(href, new URLSearchParams(merchandiseSearchParams))

  // Why save bandwidth for Shopify?
  const unoptimized = true

  return (
    <li className={cn('flex shrink-0 flex-col gap-2 overflow-hidden text-sm uppercase', className)}>
      <Link className='bg-card relative aspect-[2/3] overflow-hidden rounded-lg' href={url} onClick={closeCart} title={item.merchandise.product.title}>
        {item.merchandise.product.featuredImage && (
          <Image
            fill={true}
            unoptimized={unoptimized}
            sizes='10rem'
            className='object-cover'
            src={item.merchandise.product.featuredImage?.url.replace('.jpg', `_${10 * 16 * 2}x.jpg`)}
            alt={item.merchandise.product.featuredImage?.altText || item.merchandise.product.title}
          />
        )}
      </Link>
      <div className='flex flex-col gap-1 px-1.5'>
        <h3>
          <Link href={url} onClick={closeCart} title={item.merchandise.product.title} className='no-underline'>
            {item.merchandise.product.title}
          </Link>
        </h3>
        {item.merchandise.title !== 'Default Title' && <p>{selectedOptionsString(item.merchandise.selectedOptions, item.merchandise.title)}</p>}
        <PriceWithDiscount className='flex items-center' amount={amount} discount={discount} currencyCode={item.cost.totalAmount.currencyCode} />
      </div>
      <div className='flex w-1/2 items-center justify-between gap-1 text-center'>
        <EditCartQuantity item={item} type='minus' optimisticUpdateAction={updateCartItem} pageDto={pageDto} />
        <p className='text-center'>{item.quantity}</p>
        <EditCartQuantity item={item} type='plus' optimisticUpdateAction={updateCartItem} pageDto={pageDto} />
      </div>
      <RemoveFromCart className='px-1.5' item={item} optimisticUpdateAction={updateCartItem} pageDto={pageDto} />
    </li>
  )
}

export default CartItem
